/**
 * useBoardState - Custom hook for Teacher Cockpit persistence
 * 
 * Features:
 * - Fetches board state from Supabase on mount
 * - Debounced saves (500ms)
 * - Graceful error handling with toast feedback
 * - Continues working locally if API fails
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';

const DEFAULT_BOARD_STATE = {
    contentObjective: '',
    languageObjective: '',
    noiseLevel: 'partner',
    timerPresets: [60, 180, 300, 600],
    lastTimerSeconds: 300,
    pinnedSettings: {
        showObjectives: true,
        showTimer: true,
        showNowNext: true,
        showNoise: true
    }
};

/**
 * Extract initial objectives from lesson JSON
 */
const extractObjectivesFromLesson = (lessonJson) => {
    if (!lessonJson) return { content: '', language: '' };

    // Try to get objectives from lesson_json
    const objectives = lessonJson.objectives || [];

    // Content objective is usually first
    const contentObj = objectives[0] || '';

    // Language objective - check for explicit field or use second objective
    const languageObj = lessonJson.language_objective || objectives[1] || '';

    return {
        content: typeof contentObj === 'string' ? contentObj : contentObj.text || '',
        language: typeof languageObj === 'string' ? languageObj : languageObj.text || ''
    };
};

/**
 * Simple toast notification (uses browser alert as fallback)
 * In production, integrate with your toast library
 */
const showToast = (message, type = 'info') => {
    // Check if there's a global toast function
    if (typeof window !== 'undefined' && window.showToast) {
        window.showToast(message, type);
        return;
    }

    // Console fallback for errors
    if (type === 'error') {
        console.warn(`[BoardState] ${message}`);
    }
};

const useBoardState = ({
    lessonId,
    versionNumber = null,
    userId,
    lessonJson = null
}) => {
    // State
    const [boardState, setBoardState] = useState(DEFAULT_BOARD_STATE);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [lastSaved, setLastSaved] = useState(null);
    const [error, setError] = useState(null);
    const [persistenceAvailable, setPersistenceAvailable] = useState(true);

    // Debounce timer ref
    const saveTimeoutRef = useRef(null);
    const pendingStateRef = useRef(null);

    // Fetch board state on mount
    useEffect(() => {
        const fetchBoardState = async () => {
            if (!lessonId || !userId) {
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                const params = new URLSearchParams({
                    lesson_id: lessonId,
                    user_id: userId
                });
                if (versionNumber !== null) {
                    params.set('version_number', versionNumber);
                }

                const response = await api.get(`/api/production/board-state?${params}`);

                if (response.data) {
                    const data = response.data;

                    // Check if we got real data or just defaults
                    const hasRealData = data.content_objective || data.language_objective;

                    if (hasRealData) {
                        // Use saved state
                        setBoardState({
                            contentObjective: data.content_objective || '',
                            languageObjective: data.language_objective || '',
                            noiseLevel: data.noise_level || 'partner',
                            timerPresets: data.timer_presets || [60, 180, 300, 600],
                            lastTimerSeconds: data.last_timer_seconds || 300,
                            pinnedSettings: data.pinned_settings || DEFAULT_BOARD_STATE.pinnedSettings
                        });
                    } else {
                        // Initialize from lesson JSON
                        const extracted = extractObjectivesFromLesson(lessonJson);
                        const initialState = {
                            contentObjective: extracted.content || '',
                            languageObjective: extracted.language || '',
                            noiseLevel: 'partner',
                            timerPresets: [60, 180, 300, 600],
                            lastTimerSeconds: 300,
                            pinnedSettings: DEFAULT_BOARD_STATE.pinnedSettings
                        };
                        setBoardState(initialState);

                        // Save initial state to Supabase (fire and forget)
                        saveToSupabase(initialState).catch(() => { });
                    }
                }

                setPersistenceAvailable(true);
                setError(null);
            } catch (err) {
                console.error('Failed to fetch board state:', err);

                // Check if it's a table-missing error
                const errorMessage = err.response?.data?.detail || err.message || '';
                if (errorMessage.includes('relation') && errorMessage.includes('does not exist')) {
                    setError('Supabase table not found. Run the migration SQL.');
                    showToast('Database table missing - changes won\'t persist', 'error');
                } else if (err.response?.status === 503) {
                    setError('Database not available');
                    showToast('Database unavailable - working locally', 'error');
                } else {
                    setError(err.message);
                }

                setPersistenceAvailable(false);

                // Fall back to lesson JSON extraction
                const extracted = extractObjectivesFromLesson(lessonJson);
                setBoardState({
                    ...DEFAULT_BOARD_STATE,
                    contentObjective: extracted.content || DEFAULT_BOARD_STATE.contentObjective,
                    languageObjective: extracted.language || DEFAULT_BOARD_STATE.languageObjective
                });
            } finally {
                setIsLoading(false);
            }
        };

        fetchBoardState();
    }, [lessonId, versionNumber, userId]);

    // Save to Supabase
    const saveToSupabase = useCallback(async (stateToSave) => {
        if (!lessonId || !userId) return;

        // Skip if persistence is unavailable
        if (!persistenceAvailable) {
            return;
        }

        try {
            setIsSaving(true);

            await api.post(`/api/production/board-state?user_id=${encodeURIComponent(userId)}`, {
                lesson_id: lessonId,
                version_number: versionNumber,
                content_objective: stateToSave.contentObjective,
                language_objective: stateToSave.languageObjective,
                noise_level: stateToSave.noiseLevel,
                timer_presets: stateToSave.timerPresets,
                last_timer_seconds: stateToSave.lastTimerSeconds,
                pinned_settings: stateToSave.pinnedSettings
            });

            setLastSaved(new Date());
            setError(null);
        } catch (err) {
            console.error('Failed to save board state:', err);

            const errorMessage = err.response?.data?.detail || err.message || '';

            // Check for specific errors
            if (errorMessage.includes('relation') && errorMessage.includes('does not exist')) {
                setError('Database table missing');
                setPersistenceAvailable(false);
                showToast('Settings won\'t persist - database table missing', 'error');
            } else if (err.response?.status === 503) {
                setError('Database not available');
                setPersistenceAvailable(false);
                showToast('Couldn\'t save - database unavailable', 'error');
            } else {
                setError(err.message);
                showToast('Couldn\'t save settings - working locally', 'error');
            }
        } finally {
            setIsSaving(false);
        }
    }, [lessonId, versionNumber, userId, persistenceAvailable]);

    // Debounced save (500ms delay)
    const debouncedSave = useCallback((newState) => {
        pendingStateRef.current = newState;

        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        saveTimeoutRef.current = setTimeout(() => {
            if (pendingStateRef.current) {
                saveToSupabase(pendingStateRef.current);
                pendingStateRef.current = null;
            }
        }, 500);
    }, [saveToSupabase]);

    // Update functions
    const updateObjective = useCallback((type, value) => {
        setBoardState(prev => {
            const newState = {
                ...prev,
                [type === 'content' ? 'contentObjective' : 'languageObjective']: value
            };
            debouncedSave(newState);
            return newState;
        });
    }, [debouncedSave]);

    const updateNoiseLevel = useCallback((level) => {
        setBoardState(prev => {
            const newState = { ...prev, noiseLevel: level };
            debouncedSave(newState);
            return newState;
        });
    }, [debouncedSave]);

    const updateTimerSeconds = useCallback((seconds) => {
        setBoardState(prev => {
            const newState = { ...prev, lastTimerSeconds: seconds };
            debouncedSave(newState);
            return newState;
        });
    }, [debouncedSave]);

    const updatePinnedSettings = useCallback((settings) => {
        setBoardState(prev => {
            const newState = { ...prev, pinnedSettings: { ...prev.pinnedSettings, ...settings } };
            debouncedSave(newState);
            return newState;
        });
    }, [debouncedSave]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
            // Save any pending state immediately
            if (pendingStateRef.current && persistenceAvailable) {
                saveToSupabase(pendingStateRef.current);
            }
        };
    }, [saveToSupabase, persistenceAvailable]);

    return {
        // State
        boardState,
        isLoading,
        isSaving,
        lastSaved,
        error,
        persistenceAvailable,

        // Update functions
        updateObjective,
        updateNoiseLevel,
        updateTimerSeconds,
        updatePinnedSettings,

        // Force save
        forceSave: () => saveToSupabase(boardState)
    };
};

export default useBoardState;
